<template>
  <SectionSummary
    role="region"
    :aria-label="
      t('pricing_summary_for_table', {
        tableName: pricingTable.displayName || pricingTable.name || t('table'),
      }) + ` (${sectionId})`
    "
    aria-live="polite"
    aria-atomic="true"
  >
    <SectionSummaryRow v-for="summary in firstSectionSummaries" :key="summary.name">
      <template #description>
        <SectionSummaryText :text="getSummaryDisplayName(summary) || ''" aria-hidden="true" />
      </template>
      <template #value>
        <SectionSummaryText :text="getSummaryValueText(summary) || ''" aria-hidden="true" />
        <span class="sr-only">{{ getSummaryAriaLabelComputed(summary) }}</span>
      </template>
    </SectionSummaryRow>
    <SectionSummaryDivider v-if="firstSectionSummaries.length" />
    <SectionSummaryRow v-if="showTotalPriceSummary && totalPriceSummary">
      <template #description>
        <SectionSummaryText
          :text="getSummaryDisplayName(totalPriceSummary) || ''"
          aria-hidden="true"
        />
      </template>
      <template #value>
        <SectionSummaryText
          :text="getSummaryValueText(totalPriceSummary)"
          :data-external="`editor-pricing-table-section-summary-extended-summary-total-price-value-text`"
          aria-hidden="true"
          align-right
        />
        <span class="sr-only">{{ getSummaryAriaLabelComputed(totalPriceSummary) }}</span>
      </template>
    </SectionSummaryRow>
    <SectionSummaryRow v-if="showDiscount && hasValue(sectionSummary.discount?.value || '')">
      <template #description>
        <SectionSummaryText :text="sectionSummary.discount?.displayName || ''" aria-hidden="true" />
      </template>
      <template #value>
        <SectionSummaryText :text="discountValueText" red align-right aria-hidden="true" />
        <span class="sr-only">{{ getDiscountAriaLabelComputed }}</span>
      </template>
    </SectionSummaryRow>
    <SectionSummaryRow v-if="showTax">
      <template #description>
        <SectionSummaryText :text="sectionSummary.tax?.displayName || ''" aria-hidden="true" />
      </template>
      <template #value>
        <SectionSummaryText :text="taxValueText" align-right aria-hidden="true" />
        <span class="sr-only">{{ getTaxAriaLabelComputed }}</span>
      </template>
    </SectionSummaryRow>

    <SectionSummaryRow>
      <template #description>
        <SectionSummaryText
          :text="sectionSummary.price?.displayName || ''"
          bold
          aria-hidden="true"
        />
      </template>
      <template #value>
        <SectionSummaryText :text="subTotalValueText" bold align-right aria-hidden="true" />
        <span class="sr-only">{{ getSubtotalAriaLabelComputed }}</span>
      </template>
    </SectionSummaryRow>
  </SectionSummary>
</template>
<script lang="ts" setup>
import type { Field, PricingTable } from '@getaccept/editor-lib';
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePricingTableSectionSummary } from '@getaccept/editor-lib/src/composables/pricing-table-section-summary.composable';
import SectionSummary from '@getaccept/editor-lib/src/components/pricing-table/section-summary/SectionSummary.vue';
import SectionSummaryRow from '@getaccept/editor-lib/src/components/pricing-table/section-summary/SectionSummaryRow.vue';
import SectionSummaryText from '@getaccept/editor-lib/src/components/pricing-table/section-summary/SectionSummaryText.vue';
import SectionSummaryDivider from '@getaccept/editor-lib/src/components/pricing-table/section-summary/SectionSummaryDivider.vue';
import {
  createSummaryAriaLabel,
  createDiscountAriaLabel,
  createTaxAriaLabel,
  createSubtotalAriaLabel,
} from '@getaccept/editor-lib/src/helpers/pricing-table/pricing-table-accessibility.helper';
import { useInjectEditorViewState } from '../../composables/editor-view-state.composable';
const { productProperties } = useInjectEditorViewState();
const { t } = useI18n();

const props = defineProps<{
  pricingTable: PricingTable;
  sectionId: string;
  fields: Field[];
}>();

const { pricingTable, sectionId, fields } = toRefs(props);

const {
  sectionSummary,
  showDiscount,
  showTax,
  discountValueText,
  taxValueText,
  subTotalValueText,
  enabledSummaries,
  getSummaryDisplayName,
  getSummaryValueText,
  totalPriceSummary,
  showTotalPriceSummary,
  hasValue,
} = usePricingTableSectionSummary(pricingTable, sectionId, fields, productProperties);

const firstSectionSummaries = computed(() =>
  enabledSummaries.value.filter(({ columnId }) => columnId !== totalPriceSummary.value?.columnId)
);

const getSummaryAriaLabelComputed = (summary: any) =>
  createSummaryAriaLabel(summary, pricingTable.value);

const getDiscountAriaLabelComputed = computed(() =>
  createDiscountAriaLabel(sectionSummary.value, pricingTable.value)
);

const getTaxAriaLabelComputed = computed(() =>
  createTaxAriaLabel(sectionSummary.value, pricingTable.value)
);

const getSubtotalAriaLabelComputed = computed(() =>
  createSubtotalAriaLabel(sectionSummary.value, pricingTable.value)
);
</script>
